import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { onMount } from 'lp-hoc'
import {
  ContentBlock,
  FormBlock,
  PortalSupport,
  ExternalLink,
} from 'components'
import { Link } from 'react-router'
import { SignInForm } from '../forms'
import * as apiActions from 'api-actions'
import * as actions from '../actions'
import { selectors as globalSelectors } from 'school-portal-reducer'
import * as Types from 'types'
import { SCHOOL_PORTAL_PATH, SIGN_IN_ROUTE, ARTIST_PORTAL_ROUTE } from 'config'
import { useCommunity, apiUrlBuilder, displaySubmitFailure } from 'utils'
import { get, isEmpty } from 'lodash'
import backArrow from 'images/back.svg'
import * as LS from 'local-storage'
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google'
const propTypes = {
  location: PropTypes.object.isRequired, // provided by react router
  signIn: PropTypes.func.isRequired,
  signInSuccess: PropTypes.func.isRequired,
  communityConfigSso: PropTypes.object,
  signInViaGoogleSso: PropTypes.func.isRequired,
}

const defaultProps = {
  communityConfigSso: {},
}

function SignIn({
  signInSuccess,
  signIn,
  signInViaGoogleSso,
  communityConfigSso,
  location,
}) {
  const community = useCommunity()
  const { subdomain } = community
  const { href, title, text, logo } = communityConfigSso
  const ssoEnabled = !isEmpty(communityConfigSso)
  const googleLoginEnabled = community.googleLoginEnabled
  const [displayEmailLogin, setDisplayEmailLogin] = useState(false)
  const [displayGoogleLoginError, setDisplayGoogleLoginError] = useState(false)

  const partnerRegisterEnabled = community.partnerRegisterEnabled
  const teachingArtistRegisterEnabled =
    community.teachingArtist.enabled && community.teachingArtist.registerEnabled

  if (subdomain === 'chicago' && !ssoEnabled) return null

  const redirectUrl = get(location, 'state.redirectUrl')

  function googleSsoLogin(token) {
    signInViaGoogleSso(token)
      .then((response) => {
        signInSuccess({ ...response, redirectUrl })
      })
      .catch(() => {
        setDisplayGoogleLoginError(true)
      })
  }

  return (
    <ContentBlock>
      <FormBlock>
        <div className="form-block-inner">
          <div className="sign-in-options">
            {!displayGoogleLoginError ? (
              <div>
                <h1>
                  Sign in to your artlook<sup>®</sup> account
                </h1>
                {displayEmailLogin ? (
                  <button
                    type="button"
                    onClick={() => setDisplayEmailLogin(false)}
                    className="link-black back"
                  >
                    <img alt="" src={backArrow} />
                    Back to Sign In Options
                  </button>
                ) : (
                  <React.Fragment>
                    {ssoEnabled && (
                      <p>
                        <ExternalLink
                          href={`${apiUrlBuilder(subdomain) + href}`}
                          title={title}
                          className="button-primary sso-login"
                          onClick={() => {
                            // Preserve a deep-link destination (e.g. a
                            // notification email link) across the external SSO
                            // round-trip, since location.state is lost when we
                            // leave the SPA. Falls back to the dashboard.
                            LS.setSsoRedirect(
                              redirectUrl || 'school-portal/dashboard'
                            )
                            return true
                          }}
                        >
                          {logo && <img alt="" src={logo.secureUrl} />}
                          {text}
                        </ExternalLink>
                      </p>
                    )}
                    <p>
                      <button
                        type="button"
                        className="button-primary-outline"
                        onClick={() => setDisplayEmailLogin(true)}
                        data-cy="sign-in-with-email"
                      >
                        Sign in with Email
                      </button>
                    </p>
                    {googleLoginEnabled && (
                      <div className="googleSignInContainer">
                        <GoogleOAuthProvider
                          clientId={process.env.GOOGLE_OAUTH_CLIENT_ID}
                        >
                          <GoogleLogin
                            onSuccess={(response) => {
                              googleSsoLogin(response)
                            }}
                            size="medium"
                            width="400"
                            onError={() => {
                              setDisplayGoogleLoginError(false)
                            }}
                          />
                        </GoogleOAuthProvider>
                      </div>
                    )}
                  </React.Fragment>
                )}
              </div>
            ) : (
              <div>
                <h1>Oops! We couldn’t log you in.</h1>
                <p>
                  Double-check that you're using the right Google account. Or,
                  reach out to&nbsp;
                  <ExternalLink
                    href={`mailto:${community.schoolSupportEmail}?subject=Login Issues&body=I'm having trouble accessing the Artlook`}
                  >
                    {community.schoolSupportEmail}
                  </ExternalLink>
                  &nbsp; for more help or to set up an account.
                </p>
                <button
                  type="button"
                  onClick={() => setDisplayGoogleLoginError(false)}
                  className="button-primary"
                >
                  Back to Sign In Options
                </button>
              </div>
            )}
          </div>
          {displayEmailLogin && (
            <SignInForm
              onSubmit={
                // Pass params to onSubmitSuccess handler
                (params) =>
                  signIn(params).then((response) => ({
                    ...params,
                    ...response,
                  }))
              }
              onSubmitSuccess={(response) =>
                signInSuccess({ ...response, redirectUrl })
              }
              onSubmitFail={displaySubmitFailure}
              initialValues={{ portal: SCHOOL_PORTAL_PATH }}
            />
          )}
          <div className="register-links">
            {(() => {
              if (!partnerRegisterEnabled && !teachingArtistRegisterEnabled) {
                return null
              }
              if (partnerRegisterEnabled && !teachingArtistRegisterEnabled) {
                return (
                  <p>
                    Register for an{' '}
                    <Link to={`${SIGN_IN_ROUTE}/register`}>Organization</Link>{' '}
                    Account
                  </p>
                )
              }
              if (!partnerRegisterEnabled && teachingArtistRegisterEnabled) {
                return (
                  <p>
                    Register for a{' '}
                    <Link to={`${ARTIST_PORTAL_ROUTE}/register`}>
                      Teaching Artist
                    </Link>{' '}
                    Account
                  </p>
                )
              }
              return (
                <p>
                  Register for an{' '}
                  <Link to={`${SIGN_IN_ROUTE}/register`}>Organization</Link> or{' '}
                  <Link to={`${ARTIST_PORTAL_ROUTE}/register`}>
                    Teaching Artist
                  </Link>{' '}
                  Account
                </p>
              )
            })()}
          </div>
        </div>
      </FormBlock>
      <PortalSupport
        supportEmail={community.schoolSupportEmail}
        portalType={Types.PORTAL.SCHOOL_PORTAL}
      />
    </ContentBlock>
  )
}

SignIn.propTypes = propTypes
SignIn.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    communityConfigSso: globalSelectors.communityConfigSso(state),
  }
}

const mapDispatchToProps = {
  signInSuccess: actions.signInSuccess,
  fetchSchoolOptions: apiActions.fetchSchoolOptions,
  signIn: apiActions.signIn,
  signInViaGoogleSso: apiActions.signInViaGoogleSso,
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  onMount('fetchSchoolOptions')
)(SignIn)
